import { useEffect, useMemo, useRef, useState } from 'react'
import { api } from '../../../api/client'
import type { OnboardingRecipientHistory } from '../../../api/onboardingTypes'
import { parseOnboardingEmails } from './directIntakeLogic'

const CACHE_MS = 30_000
const DEBOUNCE_MS = 500

/** Read-only hints, never create eligibility or consent. Start still rechecks server history. */
export function useRecipientHighlights(input: string, known: OnboardingRecipientHistory | null) {
  const cache = useRef(new Map<string, { history: OnboardingRecipientHistory; at: number }>())
  const pending = useRef(new Map<string, Promise<OnboardingRecipientHistory>>())
  const [histories, setHistories] = useState<Record<string, OnboardingRecipientHistory>>({})
  const [unavailable, setUnavailable] = useState(false)
  // Keep complete tokens visible even while the next token is unfinished.
  // The strict intake parser still refuses empty/invalid/duplicate tokens on Start.
  const emailsKey = useMemo(() => {
    const tokens = input.split(',')
    if (tokens.length > 200) return '[]'
    const emails = tokens.flatMap((token) => {
      const parsed = parseOnboardingEmails(token)
      return parsed.error ? [] : parsed.emails
    })
    return JSON.stringify([...new Set(emails)])
  }, [input])
  const emails: string[] = useMemo(() => JSON.parse(emailsKey), [emailsKey])
  const multiple = input.includes(',')

  useEffect(() => {
    if (!known) return
    cache.current.set(known.email, { history: known, at: Date.now() })
    setHistories((current) => ({ ...current, [known.email]: known }))
  }, [known])

  useEffect(() => {
    const retained = new Set(emails)
    for (const email of cache.current.keys()) if (!retained.has(email)) cache.current.delete(email)
    setHistories((previous) => Object.fromEntries(Object.entries(previous).filter(([email]) => retained.has(email))))
    if (!multiple || !emails.length) return
    let current = true
    setUnavailable(false)
    const timer = setTimeout(() => {
      const missing = emails.filter((email) => {
        const entry = cache.current.get(email)
        return (!entry || Date.now() - entry.at >= CACHE_MS) && !pending.current.has(email)
      })
      if (missing.length) {
        // One set-based bounded read after typing pauses: no recipient locks or audit expansion.
        const batch = api.onboardingRecipientHighlights(missing)
        for (const email of missing) {
          const promise = batch.then((result) => {
            const history = result.find((row) => row.email === email)
            if (!history) throw new Error('Incomplete recipient history preview')
            if (retained.has(email) && current) cache.current.set(email, { history, at: Date.now() })
            return history
          }).finally(() => { pending.current.delete(email) })
          pending.current.set(email, promise)
        }
      }
      const lookups = emails.map((email) => pending.current.get(email)
        ?? Promise.resolve(cache.current.get(email)?.history))
      void Promise.all(lookups).then((results) => {
        if (!current) return
        setHistories((previous) => {
          const next = { ...previous }
          for (const history of results) if (history) next[history.email] = history
          return next
        })
      }).catch(() => { if (current) setUnavailable(true) })
    }, DEBOUNCE_MS)
    return () => { current = false; clearTimeout(timer) }
  }, [multiple, emails])

  return { histories: emails.flatMap((email) => histories[email] ? [histories[email]] : []), unavailable }
}
